import { Card, Chip, Link, Meter, Skeleton } from "@heroui/react";

import { useLocale, useTranslations } from "@/src/i18n/i18n";
import type { Sla } from "@/src/pages/Dashboard/sla";
import {
  sla,
  SLA_HOURS,
  SLA_KEY,
  SLA_TONE,
  slaDuration,
} from "@/src/pages/Dashboard/sla";
import type { Report } from "@/src/pages/Reports/reports.type";
import { ROUTES } from "@/src/routes/paths";

/**
 * Piste teintée du **même** ton que le remplissage, et non du gris neutre de
 * HeroUI : l'état se lit alors sur toute la largeur de la carte, y compris sur
 * la part qui reste à courir.
 *
 * `--meter-fill` est posée par la variante de couleur sur la racine du `Meter`.
 * Un style en ligne est ici la voie la plus courte — aucune classe ne sait
 * mélanger une variable dont la valeur n'est connue qu'à l'exécution — et la
 * constante est hors du composant pour ne pas recréer l'objet à chaque rendu.
 */
const TRACK_STYLE = {
  backgroundColor: "color-mix(in oklab, var(--meter-fill) 22%, transparent)",
};

type WatchBannerProps = {
  /** `undefined` tant que le serveur n'a pas répondu — pas `0`. */
  pending: number | undefined;
  /** Le plus ancien signalement en attente : c'est lui qui donne l'échéance. */
  oldest: Report | undefined;
  isError: boolean;
};

/**
 * Le poste de garde : la seule chose de cet écran qui demande une décision.
 *
 * Un tableau de bord d'administration ne sert pas à contempler des volumes, il
 * sert à répondre en trois secondes à « qu'est-ce qui presse ? ». D'où un seul
 * grand chiffre — la file en attente — et, à côté, la seule échéance que ce
 * produit connaisse : les 24 h de la directive App Store 1.2.
 *
 * ⚠️ **La jauge est le bord bas de la carte, sur toute sa largeur.** Ce n'est
 * pas de la décoration : c'est l'état du poste, lisible d'un coup d'œil de
 * l'autre bout de la pièce, et il ne coupe pas la lecture du chiffre. La file
 * vide en garde la trace — un filet vert plein, plutôt qu'une jauge à zéro qui
 * serait invisible et ne dirait donc rien.
 *
 * ⚠️ **La couleur ne dit jamais l'état à elle seule** : la pastille porte le
 * mot, pour qui ne distingue pas le rouge du vert comme pour qui lit une
 * capture en noir et blanc.
 */
export function WatchBanner({ pending, oldest, isError }: WatchBannerProps) {
  const t = useTranslations("dashboard");
  const { locale } = useLocale();

  // Recalculés à chaque rendu plutôt que figés dans un état : un poste laissé
  // ouvert une nuit afficherait sinon l'échéance de la veille.
  const now = Date.now();
  const state = oldest ? sla(oldest.createdAt, now) : null;

  return (
    <Card className="overflow-hidden p-0">
      <div className="relative flex flex-col gap-6 p-5 transition-colors hover:bg-surface-hover sm:flex-row sm:items-end sm:justify-between sm:gap-8 sm:p-6">
        <div className="flex flex-col gap-1">
          {pending === undefined ? (
            <Skeleton className="my-1 h-12 w-24" />
          ) : (
            /* Le seul chiffre héros de l'écran : les tuiles en dessous sont
               volontairement d'un cran plus petites, sans quoi rien ne
               dominerait et l'urgence se dissoudrait dans les volumes. */
            <p className="text-5xl leading-none font-semibold tracking-[-0.03em] sm:text-6xl">
              {new Intl.NumberFormat(locale).format(pending)}
            </p>
          )}

          <p className="text-sm text-muted">{t("pending-label")}</p>
        </div>

        <div className="flex flex-col items-start gap-2 sm:items-end">
          {isError ? (
            <p className="text-sm text-muted">{t("queue-unreadable")}</p>
          ) : pending === 0 ? (
            /* Même ordre que l'état chargé — le fait, puis le verdict : c'est
               ce qui permet de balayer les deux au même endroit. */
            <>
              <p className="text-sm">{t("queue-clear-hint")}</p>

              <Chip color="success" size="sm">
                {t("queue-clear")}
              </Chip>
            </>
          ) : state ? (
            <>
              {/* L'attente du plus ancien avant son état : c'est le fait, la
                  pastille n'en est que le verdict. */}
              <p className="text-sm">
                {t("oldest-since", {
                  duration: slaDuration(state.hours, locale),
                })}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Chip color={SLA_TONE[state.level]} size="sm">
                  {t(SLA_KEY[state.level])}
                </Chip>

                {/* Sans ce repère, la jauge du bas ne se rapporte à rien. */}
                <span className="text-xs text-muted">
                  {t("sla-target", { hours: SLA_HOURS })}
                </span>
              </div>
            </>
          ) : (
            <Skeleton className="h-5 w-48" />
          )}

          {/* ⚠️ `static` : `.link` pose `position: relative`, et sans lui
              l'`after:inset-0` s'ancrerait sur le lien au lieu du bandeau —
              seul le texte resterait cliquable. */}
          <Link
            className="static text-sm font-medium after:absolute after:inset-0"
            href={ROUTES.reports}
          >
            {t("open-queue")}
          </Link>
        </div>
      </div>

      <SlaStrip isClear={pending === 0} state={state} />
    </Card>
  );
}

/**
 * Le bord bas de la carte, qui dit l'état de la garde.
 *
 * Trois formes pour un seul objet : la jauge quand une file attend, un filet
 * vert plein quand elle est à jour, un filet neutre tant qu'on ne sait pas.
 */
function SlaStrip({ isClear, state }: { isClear: boolean; state: Sla | null }) {
  const t = useTranslations("dashboard");

  if (state && !isClear) {
    return (
      <Meter
        aria-label={t("sla-label", { hours: SLA_HOURS })}
        className="gap-0"
        color={SLA_TONE[state.level]}
        maxValue={SLA_HOURS}
        value={state.hours}
      >
        <Meter.Track className="h-1.5 rounded-none" style={TRACK_STYLE}>
          <Meter.Fill className="rounded-none" />
        </Meter.Track>
      </Meter>
    );
  }

  return (
    <div
      aria-hidden
      className={`h-1.5 w-full ${isClear ? "bg-success" : "bg-default"}`}
    />
  );
}
